// VegTiles: the tree / shrub crown packs (trees.js Forest, shrubs.js Shrubs)
// — CPK1 per tile (bake.py write_crowns, crownpack.js decodeCrowns;
// meta.crowns, meta.crowns.shrubs = {layout: 'CPK1', tile_m, dir, list:
// [[i, j, n], ...]}): tile (i, j) = <dir>/<i>_<j>.cpk. Each tile owns the
// slots start..start+n of the host's world-wide arrays, start = the running
// sum of n in list order, so a loaded crown never moves; every per-crown
// choice keys on its position hash (crownHash), never on the slot.
// want() asks for the tiles meeting a disc: 'near'
// (the draw range: the beam-in latch waits for settled()) or 'bulk' (the
// prefetch ring, the scope wedge, the Map's view) — the queue ranks a bulk
// tile by its rect (need, never dropped). A 'near' want of a tile queued as
// bulk fetches it again at 'near'; the first landing wins. A failed tile is
// asked again after RETRY_MS.
import { fetchAsset } from './assetq.js';

const RETRY_MS = 15000;
const _nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const IDLE = 0, LOADING = 1, IN = 2, FAILED = 3;
export class VegTiles {
  // rec: bytes per record (the need's estimate); onTile(t, ab) once per
  // landed tile — it decodes and THROWS on a malformed pack (the tile fails)
  constructor(idx, baseUrl, rec, onTile) {
    if (!idx || idx.layout !== 'CPK1') throw new Error(`crown pack index ${idx && idx.layout} is not CPK1: rebuild this world (Build)`);
    this.m = idx.tile_m;
    this.url = `${baseUrl}/${idx.dir}/`;
    this.rec = rec;
    this.onTile = onTile;
    this.map = new Map();   // "i_j" -> {i, j, start, n, st, prio, t}
    let start = 0;
    for (const [i, j, n] of idx.list) { this.map.set(i + '_' + j, { i, j, start, n, st: IDLE, prio: null, t: 0 }); start += n; }
    this.count = start;
  }

  // the tiles whose square meets the disc (x, z, r)
  around(x, z, r) {
    const m = this.m, out = [];
    for (let j = Math.floor((z - r) / m); j <= Math.floor((z + r) / m); j++) {
      for (let i = Math.floor((x - r) / m); i <= Math.floor((x + r) / m); i++) {
        const t = this.map.get(i + '_' + j);
        if (!t) continue;
        const dx = Math.max(0, Math.abs(x - (i + 0.5) * m) - m / 2);
        const dz = Math.max(0, Math.abs(z - (j + 0.5) * m) - m / 2);
        if (dx * dx + dz * dz <= r * r) out.push(t);
      }
    }
    return out;
  }

  want(x, z, r, prio) { for (const t of this.around(x, z, r)) this._get(t, prio); }

  wantRect(rect) {
    const m = this.m;
    for (let j = Math.floor(rect[1] / m); j <= Math.floor(rect[3] / m); j++) {
      for (let i = Math.floor(rect[0] / m); i <= Math.floor(rect[2] / m); i++) {
        const t = this.map.get(i + '_' + j);
        if (t) this._get(t, 'bulk');
      }
    }
  }

  // the scope wedge (focus.js): every tile within range of the apex it hits
  wantWedge(focus, range) {
    const m = this.m;
    for (const t of this.around(focus.ox, focus.oz, range)) {
      if (focus.hit((t.i + 0.5) * m, (t.j + 0.5) * m, m * 0.7072, range)) this._get(t, 'bulk');
    }
  }

  settled(x, z, r) {
    for (const t of this.around(x, z, r)) if (t.st !== IN && t.st !== FAILED) return false;
    return true;
  }

  _get(t, prio) {
    if (t.st === IN) return;
    const now = _nowMs();
    if (t.st === FAILED && now - t.t < RETRY_MS) return;
    if (t.st === LOADING && (t.prio === 'near' || prio !== 'near')) return;
    t.st = LOADING;
    t.prio = prio;
    t.t = now;
    const m = this.m;
    const fail = () => {
      if (t.st === LOADING && t.prio === prio) { t.st = FAILED; t.t = _nowMs(); }
    };
    fetchAsset(`${this.url}${t.i}_${t.j}.cpk`, {
      prio, as: 'arrayBuffer',
      need: { rect: [t.i * m, t.j * m, (t.i + 1) * m, (t.j + 1) * m], m: null, bytes: t.n * this.rec, keep: true },
    }).then((ab) => {
      if (t.st === IN) return;                       // the other fetch landed first
      if (!ab) { fail(); return; }
      try { this.onTile(t, ab); } catch (e) { console.error(`[veg] ${this.url}${t.i}_${t.j}.cpk: ${e.message}`); fail(); return; }
      t.st = IN;
    }, fail);
  }
}
